export const SITE_URL = "https://akinda.io";

export type RobotsDirective =
  | "index, follow"
  | "noindex, nofollow"
  | "noindex, follow";

export interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  /** Absolute URL or path for this page. Also the canonical unless overridden. */
  url?: string;
  /** Explicit canonical. Defaults to `url`. */
  canonical?: string;
  type?: string;
  author?: string;
  robots?: RobotsDirective;
}

export type ResolvedSEO = Required<Omit<SEOProps, "canonical">> & {
  canonical: string;
};

export const DEFAULT_SEO = {
  title:
    "Akinda - Halal Investment Platform | Stock Analysis & Portfolio Management",
  description:
    "Akinda - Halal investment platform specializing in stock analysis, portfolio management, and financial insights. Discover halal stocks, expert analysis, and build a compliant investment portfolio.",
  keywords:
    "halal stocks, islamic finance, halal investing, stock analysis, portfolio management, halal investments, Sharia-compliant stocks, financial analysis, investment platform",
  image: `${SITE_URL}/Logomark.webp`,
  url: `${SITE_URL}/`,
  type: "website",
  author: "Akinda",
  robots: "index, follow",
} as const;

/**
 * Normalize to one canonical form: absolute akinda.io URL, no trailing slash
 * except root, no query string. Path case is preserved — ticker segments like
 * /symbol/AMD are canonically uppercase.
 */
export const toCanonical = (pathOrUrl: string): string => {
  const url = pathOrUrl.startsWith("http")
    ? new URL(pathOrUrl)
    : new URL(pathOrUrl, SITE_URL);
  const path = url.pathname.replace(/\/+$/, "");
  return `${SITE_URL}${path === "" ? "/" : path}`;
};

const updateMetaProperty = (property: string, content: string) => {
  let element = document.querySelector(
    `meta[property="${property}"]`,
  ) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("property", property);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
};

const updateMetaName = (name: string, content: string) => {
  let element = document.querySelector(
    `meta[name="${name}"]`,
  ) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("name", name);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
};

const updateCanonical = (href: string) => {
  let link = document.querySelector(
    'link[rel="canonical"]',
  ) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
};

export const applySEO = ({
  title,
  description,
  keywords,
  image,
  canonical,
  type,
  author,
  robots,
}: ResolvedSEO) => {
  document.title = title;

  updateMetaName("description", description);
  updateMetaName("keywords", keywords);
  updateMetaName("author", author);
  updateMetaName("robots", robots);

  updateMetaProperty("og:title", title);
  updateMetaProperty("og:description", description);
  updateMetaProperty("og:image", image);
  updateMetaProperty("og:url", canonical);
  updateMetaProperty("og:type", type);

  // Twitter card tags are name=, not property= — index.html declares them
  // that way and X's parser reads name=. Writing them as property= created a
  // duplicate set and left the name= tags stuck on homepage values.
  updateMetaName("twitter:title", title);
  updateMetaName("twitter:description", description);
  updateMetaName("twitter:image", image);
  updateMetaName("twitter:url", canonical);

  updateCanonical(canonical);
};

export const resolveSEO = (props: SEOProps): ResolvedSEO => {
  const url = props.url ?? DEFAULT_SEO.url;
  return {
    title: props.title ?? DEFAULT_SEO.title,
    description: props.description ?? DEFAULT_SEO.description,
    keywords: props.keywords ?? DEFAULT_SEO.keywords,
    image: props.image ?? DEFAULT_SEO.image,
    url,
    canonical: toCanonical(props.canonical ?? url),
    type: props.type ?? DEFAULT_SEO.type,
    author: props.author ?? DEFAULT_SEO.author,
    robots: props.robots ?? DEFAULT_SEO.robots,
  };
};
